import {
  CalendarDaysIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ReportAgreementModal from "./report-agreement-modal";
import { ReportAppointmentApprovedStatus } from "./utils";
import SMSDialog from "../../sms/dialog";
import { CopyToClipboard, Popup } from "../../../utils/Div";

export const ReportAgreement = ({ onRefresh = null, ...props }) => {
  const { report } = props;
  const [agreementModalOpen, setAgreementModalOpen] = useState(false);
  const [smsDialogOpen, setSMSDialogOpen] = useState(false);
  const { t } = useTranslation();

  const hasAgreement = Boolean(report?.appointment?.has_agreement);

  function AgreementStatusInfo() {
    return (
      <div className="space-y-2 text-sm">
        {hasAgreement ? (
          <>
            <div className="flex items-center space-x-2">
              {report?.appointment?.approved ? (
                <>
                  <CheckBadgeIcon className="inline w-5 h-5 text-green-700" />
                  <span>{t("report.agreement.status.confirmed")}</span>
                </>
              ) : (
                <>
                  <ExclamationTriangleIcon className="inline w-5 h-5 text-red-700" />
                  <span>{t("report.agreement.status.not_confirmed")}</span>
                </>
              )}
            </div>

            {report?.appointment?.text_sent ? (
              <>
                <div className="flex items-center space-x-2">
                  <CheckBadgeIcon className="inline w-5 h-5 text-green-700" />
                  <span>{t("report.agreement.status.sms_sent")}</span>
                </div>
                {report?.appointment?.approved_via_text ? (
                  <div className="flex items-center space-x-2">
                    <CheckBadgeIcon className="inline w-5 h-5 text-green-700" />
                    <span>{t("report.agreement.status.sms_confirmed")}</span>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="inline w-5 h-5 text-red-700" />
                <span>{t("report.agreement.status.sms_not_sent")}</span>
              </div>
            )}

            {report?.appointment?.email_sent ? (
              <div className="flex items-center space-x-2">
                <CheckBadgeIcon className="inline w-5 h-5 text-green-700" />
                <span>{t("report.agreement.status.email_sent")}</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="inline w-5 h-5 text-red-700" />
                <span>{t("report.agreement.status.email_not_sent")}</span>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center space-x-2">
            <ExclamationTriangleIcon className="inline w-5 h-5 text-amber-600" />
            <span>{t("report.agreement.status.none")}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <section
        className="lg:max-w[7xl] h-full"
        aria-labelledby="report-agreement-title"
      >
        <div
          className={`shadow-sm rounded-2xl h-full border ${
            hasAgreement ? "bg-white border-gray-200" : "bg-amber-50/80 border-amber-200"
          }`}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between px-4 py-4 sm:px-6 w-full">
            <div className="min-w-0 flex-1">
              <h2
                id="report-agreement-title"
                className="text-lg font-medium leading-6 text-gray-900 flex flex-wrap items-center gap-2"
              >
                {t("report.agreement.title")}
                <Popup content={<AgreementStatusInfo />}>
                  <ReportAppointmentApprovedStatus report={report} />
                </Popup>
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-600">
                {t("report.agreement.subtitle")}
              </p>
            </div>
            <div className="flex flex-shrink-0 gap-2">
              <button
                onClick={() => setSMSDialogOpen(true)}
                type="button"
                className="inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <PhoneIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
                SMS
              </button>
              <button
                onClick={() => setAgreementModalOpen(true)}
                type="button"
                className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <PencilIcon className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
                {hasAgreement ? t("report.agreement.edit") : t("report.agreement.setup_cta")}
              </button>
            </div>
          </div>

          {!hasAgreement ? (
            <div className="border-t border-amber-200/80 px-4 py-5 sm:px-6">
              <div className="flex gap-4 rounded-lg bg-white/90 p-4 ring-1 ring-amber-100">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                  <CalendarDaysIcon className="h-6 w-6" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {t("report.agreement.setup_title")}
                  </p>
                  <p className="mt-1 text-sm text-gray-600">
                    {t("report.agreement.setup_body")}
                  </p>
                  <button
                    type="button"
                    onClick={() => setAgreementModalOpen(true)}
                    className="mt-3 text-sm font-semibold text-blue-700 hover:text-blue-800"
                  >
                    {t("report.agreement.setup_cta")} →
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {hasAgreement ? (
            <div className="border-t border-gray-200 px-4 py-4 sm:px-6 bg-white/60">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {t("Executing Employee")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {report?.executing_employee
                      ? report.executing_employee.name
                      : t("not_chosen")}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {t("Agreement date")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {report?.appointment?.date ? (
                      <CopyToClipboard
                        path={null}
                        content={report.appointment.date}
                        type="popup"
                      />
                    ) : (
                      t("not_chosen")
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {report?.appointment?.time
                      ? t("Fixed time")
                      : t("report.agreement.time_range")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {report?.appointment?.time ? (
                      report.appointment.time
                    ) : report?.appointment?.time_from &&
                      report?.appointment?.time_too ? (
                      <CopyToClipboard
                        path={null}
                        content={
                          `${report.appointment.time_from} – ${report.appointment.time_too}`
                        }
                        type="popup"
                      />
                    ) : (
                      t("not_chosen")
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          ) : null}
        </div>
      </section>
      <ReportAgreementModal
        open={agreementModalOpen}
        onClose={() => setAgreementModalOpen(false)}
        selected={report}
        onRefresh={(data) => onRefresh && onRefresh(data)}
      />

      <SMSDialog
        open={smsDialogOpen}
        onClose={() => setSMSDialogOpen(false)}
        selected={report}
        onRefresh={(data) => onRefresh && onRefresh(data)}
        module="report"
        module_param={report?.id}
      />
    </>
  );
};
